(function () {
  'use strict';
  const MAX_BYTES = 10 * 1024 * 1024;
  const FINISHES = {
    gold: ['#8c601a', '#fae8ac', '#cba34f', '#f5dc91', '#a37529'],
    silver: ['#777f8e', '#f8fbff', '#aeb7c4', '#e8edf4', '#848d9b'],
    black: ['#101114', '#41424a', '#1f2025', '#303139', '#111216'],
    white: ['#cac8cb', '#ffffff', '#edecef', '#ffffff', '#ceccd1']
  };
  const canvas = (w, h) => {
    const el = document.createElement('canvas'); el.width = w; el.height = h; return el;
  };
  function validateFile(file) {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/i.test(file.type) || !/\.(png|jpe?g|webp)$/i.test(file.name)) throw Error('file_type');
    if (file.size > MAX_BYTES) throw Error('file_size');
    if (!file.size) throw Error('file_decode');
  }
  function sourceCanvas(image) {
    const w = image.naturalWidth || image.width, h = image.naturalHeight || image.height;
    if (!w || !h || w > 16000 || h > 16000 || w * h > 80000000) throw Error('file_dimensions');
    const ratio = Math.min(1, 900 / Math.max(w, h));
    const out = canvas(Math.max(1, Math.round(w * ratio)), Math.max(1, Math.round(h * ratio)));
    out.getContext('2d').drawImage(image, 0, 0, out.width, out.height);
    out.originalWidth = w; out.originalHeight = h;
    return out;
  }
  // Circle crops describe the original image, never the size of a phone preview.
  // At zoom 1 every corner of the source fits inside the acrylic circle.
  function circleCrop(value) {
    if (!value) return { version: 1, shape: 'circle', centerX: .5, centerY: .5, zoom: 1 };
    if (value.version !== 1 || value.shape !== 'circle' ||
        ![value.centerX, value.centerY, value.zoom].every(Number.isFinite)) throw Error('crop_invalid');
    return { version: 1, shape: 'circle', centerX: Math.max(0, Math.min(1, value.centerX)),
      centerY: Math.max(0, Math.min(1, value.centerY)), zoom: Math.max(1, Math.min(6, value.zoom)) };
  }
  function circlePlacement(source, diameter, value) {
    const crop = circleCrop(value), scale = diameter * .98 / Math.hypot(source.width, source.height) * crop.zoom;
    return { crop, scale, width: source.width * scale, height: source.height * scale,
      x: diameter / 2 - crop.centerX * source.width * scale,
      y: diameter / 2 - crop.centerY * source.height * scale };
  }
  function fullColorFace(source, value, base = 'White', size = 900) {
    const face = canvas(size, size), ctx = face.getContext('2d'), placement = circlePlacement(source, size, value);
    ctx.save(); ctx.beginPath(); ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2); ctx.clip();
    ctx.fillStyle = { White: '#ffffff', Black: '#171719', Frosted: '#eeeae2', Clear: 'rgba(255,255,255,.2)' }[base] || '#ffffff';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(source, placement.x, placement.y, placement.width, placement.height); ctx.restore();
    return face;
  }
  function artworkForMode(source, mode) {
    return mode === 'full-color' ? { image: source, cleanup: 'Full design preserved' } : prepareArtwork(source);
  }
  class CircleCropEditor {
    constructor(dialog, strings) {
      this.dialog = dialog; this.strings = strings; this.target = dialog.querySelector('[data-circle-canvas]');
      this.range = dialog.querySelector('[data-circle-zoom]'); this.output = dialog.querySelector('[data-circle-zoom-label]');
      this.pointers = new Map(); this.active = false;
      this.range.addEventListener('input', () => this.setZoom(Number(this.range.value) / 100));
      dialog.querySelectorAll('[data-circle-step]').forEach(button => button.addEventListener('click', () => this.setZoom(this.crop.zoom + Number(button.dataset.circleStep))));
      dialog.querySelector('[data-circle-fit]').addEventListener('click', () => { this.crop = circleCrop(); this.render(); this.startGesture(); });
      dialog.querySelector('[data-circle-reset]').addEventListener('click', () => { this.crop = circleCrop(this.initial); this.render(); this.startGesture(); });
      dialog.querySelector('[data-circle-cancel]').addEventListener('click', () => this.cancel());
      dialog.querySelector('[data-circle-use]').addEventListener('click', () => this.finish(circleCrop(this.crop)));
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.cancel(); });
      dialog.addEventListener('close', () => { if (this.active) this.cancel(); });
      const point = event => {
        const rect = this.target.getBoundingClientRect();
        return { x: (event.clientX - rect.left) * 640 / rect.width, y: (event.clientY - rect.top) * 640 / rect.height };
      };
      this.target.addEventListener('pointerdown', event => {
        if (!this.active || event.button > 0 || this.pointers.size >= 2) return;
        this.target.focus({ preventScroll: true }); this.pointers.set(event.pointerId, point(event));
        this.target.setPointerCapture(event.pointerId); this.startGesture(); event.preventDefault();
      });
      this.target.addEventListener('pointermove', event => {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, point(event));
        const points = [...this.pointers.values()], first = points[0], start = this.gesture;
        const middle = points.length === 2 ? { x: (first.x + points[1].x) / 2, y: (first.y + points[1].y) / 2 } : first;
        const distance = points.length === 2 ? Math.hypot(first.x - points[1].x, first.y - points[1].y) : 0;
        const zoom = Math.max(1, Math.min(6, start.crop.zoom * (start.distance ? distance / start.distance : 1)));
        const next = circleCrop({ ...start.crop, zoom }), oldScale = circlePlacement(this.source, 512, start.crop).scale;
        const newScale = circlePlacement(this.source, 512, next).scale;
        next.centerX = start.crop.centerX + (start.middle.x - 320) / (this.source.width * oldScale) - (middle.x - 320) / (this.source.width * newScale);
        next.centerY = start.crop.centerY + (start.middle.y - 320) / (this.source.height * oldScale) - (middle.y - 320) / (this.source.height * newScale);
        this.crop = circleCrop(next); this.render(); event.preventDefault();
      });
      const release = event => {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.delete(event.pointerId);
        if (this.target.hasPointerCapture(event.pointerId)) this.target.releasePointerCapture(event.pointerId);
        this.startGesture();
      };
      this.target.addEventListener('pointerup', release); this.target.addEventListener('pointercancel', release);
      this.target.addEventListener('lostpointercapture', release);
      this.target.addEventListener('keydown', event => {
        if (!this.active) return;
        const step = event.shiftKey ? 20 : 4, scale = circlePlacement(this.source, 512, this.crop).scale;
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
          const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
          const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
          this.crop = circleCrop({ ...this.crop, centerX: this.crop.centerX - dx / (this.source.width * scale), centerY: this.crop.centerY - dy / (this.source.height * scale) });
          this.render();
        } else if (event.key === '+' || event.key === '=') this.setZoom(this.crop.zoom + .1);
        else if (event.key === '-' || event.key === '_') this.setZoom(this.crop.zoom - .1);
        else if (event.key === 'Home') { this.crop = circleCrop(); this.render(); this.startGesture(); }
        else return;
        event.preventDefault();
      });
    }
    startGesture() {
      const points = [...this.pointers.values()]; if (!points.length) { this.gesture = null; return; }
      this.gesture = { crop: circleCrop(this.crop), middle: points.length === 2 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0],
        distance: points.length === 2 ? Math.max(1, Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)) : 0 };
    }
    setZoom(zoom) { if (!this.active) return; this.crop = circleCrop({ ...this.crop, zoom }); this.render(); this.startGesture(); }
    edit(source, accepted, base = 'White') {
      this.cancel(); this.source = source; this.base = base; this.crop = circleCrop(accepted); this.initial = circleCrop(this.crop);
      this.returnFocus = this.dialog.ownerDocument.activeElement; this.active = true; this.render();
      const pending = new Promise(resolve => { this.resolve = resolve; });
      this.dialog.showModal(); this.target.focus({ preventScroll: true }); return pending;
    }
    finish(result) {
      if (!this.active) return;
      this.active = false; this.pointers.clear(); this.gesture = null;
      const resolve = this.resolve; this.resolve = null;
      if (this.dialog.open) this.dialog.close();
      if (this.returnFocus && this.returnFocus.isConnected !== false) this.returnFocus.focus({ preventScroll: true });
      resolve(result);
    }
    cancel() { this.finish(null); }
    render() {
      const ctx = this.target.getContext('2d'), placement = circlePlacement(this.source, 512, this.crop);
      ctx.clearRect(0, 0, 640, 640); ctx.fillStyle = '#f0edf3'; ctx.fillRect(0, 0, 640, 640);
      ctx.drawImage(this.source, 64 + placement.x, 64 + placement.y, placement.width, placement.height);
      ctx.save(); ctx.beginPath(); ctx.arc(320, 320, 256, 0, Math.PI * 2); ctx.clip();
      ctx.fillStyle = '#f0edf3'; ctx.fillRect(64, 64, 512, 512); ctx.restore();
      ctx.drawImage(fullColorFace(this.source, this.crop, this.base, 512), 64, 64);
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, 640, 640); ctx.moveTo(576, 320); ctx.arc(320, 320, 256, 0, Math.PI * 2);
      ctx.clip('evenodd'); ctx.fillStyle = 'rgba(32,20,43,.55)'; ctx.fillRect(0, 0, 640, 640); ctx.restore();
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(320, 320, 256, 0, Math.PI * 2); ctx.stroke();
      this.range.value = String(Math.round(this.crop.zoom * 100)); this.output.textContent = this.range.value + '%';
    }
  }
  // Border-connected matte regions are removed independently, so a black/white
  // logo sheet keeps both white-on-black and black-on-white marks and their holes.
  function illuminatedArtwork(source) {
    const w=source.width,h=source.height,ctx=source.getContext('2d'),pixels=ctx.getImageData(0,0,w,h).data,total=w*h,cores=new Uint8Array(total);
    let border=0,darkBorder=0,bright=0,colored=0,halo=0,neutralHalo=0,feather=0,left=w,right=-1,top=h,bottom=-1;
    for(let i=0;i<total;i++){
      const at=i*4,max=Math.max(pixels[at],pixels[at+1],pixels[at+2]),min=Math.min(pixels[at],pixels[at+1],pixels[at+2]),alpha=pixels[at+3];
      if(i<w||i>=total-w||i%w===0||i%w===w-1){border++;if(alpha<64||max<70)darkBorder++;}
      if(alpha<100)continue;
      if(max>=230&&min>=140){cores[i]=1;bright++;const x=i%w,y=Math.floor(i/w);left=Math.min(left,x);right=Math.max(right,x);top=Math.min(top,y);bottom=Math.max(bottom,y);}
      if(max>=150&&max-min>=70)colored++;
      if(max>=50&&max<170&&max-min>=40)halo++;
      if(max>=50&&max<170&&max-min<24)neutralHalo++;
    }
    for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
      const i=y*w+x,at=i*4;if(pixels[at+3]<8||pixels[at+3]>=200||Math.max(pixels[at],pixels[at+1],pixels[at+2])<150)continue;
      let adjacent=false;for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++)if(cores[i+dy*w+dx])adjacent=true;if(!adjacent)feather++;
    }
    const colorLight=colored>=total*.015&&(halo>=total*.015||feather>=total*.01),whiteLight=colored<total*.005&&(neutralHalo>=total*.005&&neutralHalo>=bright*.2||feather>=total*.01);
    if(darkBorder/border<.9||bright<total*.003||bright>total*.35||!colorLight&&!whiteLight)return null;
    // Distance from luminous cores separates the physical logo face from the
    // photographic wall and baked glow. No new contour strokes are generated.
    const distance=new Float32Array(total);for(let i=0;i<total;i++)distance[i]=cores[i]?0:10000;
    for(let y=0;y<h;y++)for(let x=0;x<w;x++){const i=y*w+x;let d=distance[i];if(x)d=Math.min(d,distance[i-1]+1);if(y)d=Math.min(d,distance[i-w]+1);if(x&&y)d=Math.min(d,distance[i-w-1]+1.414);if(x<w-1&&y)d=Math.min(d,distance[i-w+1]+1.414);distance[i]=d;}
    for(let y=h-1;y>=0;y--)for(let x=w-1;x>=0;x--){const i=y*w+x;let d=distance[i];if(x<w-1)d=Math.min(d,distance[i+1]+1);if(y<h-1)d=Math.min(d,distance[i+w]+1);if(x<w-1&&y<h-1)d=Math.min(d,distance[i+w+1]+1.414);if(x&&y<h-1)d=Math.min(d,distance[i+w-1]+1.414);distance[i]=d;}
    const radius=Math.max(4,Math.min(12,Math.max(w,h)*.0125)),margin=Math.ceil(radius);left=Math.max(0,left-margin);top=Math.max(0,top-margin);right=Math.min(w-1,right+margin);bottom=Math.min(h-1,bottom+margin);
    const image=canvas(right-left+1,bottom-top+1),face=canvas(image.width,image.height),paint=image.getContext('2d'),facePaint=face.getContext('2d'),output=paint.createImageData(image.width,image.height),mask=facePaint.createImageData(image.width,image.height);
    const smooth=value=>{value=Math.max(0,Math.min(1,value));return value*value*(3-2*value);};
    for(let y=top;y<=bottom;y++)for(let x=left;x<=right;x++){
      const index=y*w+x,from=index*4,to=((y-top)*image.width+x-left)*4,max=Math.max(pixels[from],pixels[from+1],pixels[from+2]),alpha=pixels[from+3];
      for(let channel=0;channel<3;channel++){output.data[to+channel]=pixels[from+channel];mask.data[to+channel]=255;}
      output.data[to+3]=Math.round(alpha*smooth((max-25)/80)*smooth((radius-distance[index])/(radius*.65)));
      mask.data[to+3]=Math.round(alpha*smooth((max-80)/90)*smooth((3-distance[index])/2));
    }
    paint.putImageData(output,0,0);facePaint.putImageData(mask,0,0);image.finishMask=face;image.artworkKind='illuminated';
    return {image,backgroundRemoved:true,cleanup:'Finished artwork preserved'};
  }
  function prepareArtwork(source, removeBackground = true) {
    if(removeBackground){const illuminated=illuminatedArtwork(source);if(illuminated)return illuminated;}
    const ctx = source.getContext('2d'), w = source.width, h = source.height;
    const pixels = ctx.getImageData(0, 0, w, h), data = pixels.data;
    const distance = (i, bg) => Math.max(Math.abs(data[i] - bg[0]), Math.abs(data[i + 1] - bg[1]), Math.abs(data[i + 2] - bg[2]));
    const samples = [], groups = [];
    let transparent = 0;
    const sample = (x, y) => {
      const i = (y * w + x) * 4; samples.push(i);
      if (data[i + 3] < 240) { transparent++; return; }
      let group = groups.find(g => distance(i, g.color) < 24);
      if (!group) { group = { color: [data[i], data[i + 1], data[i + 2]], count: 0 }; groups.push(group); }
      group.count++;
    };
    const step = Math.max(1, Math.floor(Math.max(w, h) / 100));
    for (let x = 0; x < w; x += step) { sample(x, 0); sample(x, h - 1); }
    for (let y = 0; y < h; y += step) { sample(0, y); sample(w - 1, y); }
    const knockout = removeBackground && transparent / samples.length < .02;
    let labels, palette;
    if (knockout) {
      palette = groups.filter(g => g.count / samples.length >= .12).sort((a, b) => b.count - a.count).slice(0, 4).map(g => g.color);
      const coverage = samples.filter(i => data[i + 3] > 240 && palette.some(c => distance(i, c) < 38)).length / samples.length;
      if (!palette.length || coverage < .86) throw Error('background_unclear');
      labels = new Uint8Array(w * h); const queue = new Int32Array(w * h); let head = 0, tail = 0;
      const seed = p => {
        if (labels[p]) return;
        const i = p * 4, label = palette.findIndex(c => distance(i, c) < 38);
        if (label >= 0) { labels[p] = label + 1; queue[tail++] = p; }
      };
      for (let x = 0; x < w; x++) { seed(x); seed((h - 1) * w + x); }
      for (let y = 0; y < h; y++) { seed(y * w); seed(y * w + w - 1); }
      const neighbors = (p, visit) => { if (p % w) visit(p - 1); if (p % w < w - 1) visit(p + 1); if (p >= w) visit(p - w); if (p < w * (h - 1)) visit(p + w); };
      while (head < tail) {
        const p = queue[head++], label = labels[p];
        neighbors(p, n => { if (!labels[n] && distance(n * 4, palette[label - 1]) < 38) { labels[n] = label; queue[tail++] = n; } });
      }
      // Extend each matte's color to its nearest enclosed gap. This removes
      // letter counters without erasing opposite-color artwork in another tile.
      head = 0;
      while (head < tail) {
        const p = queue[head++];
        neighbors(p, n => { if (!labels[n]) { labels[n] = labels[p]; queue[tail++] = n; } });
      }
    }
    let minX = w, minY = h, maxX = -1, maxY = -1, filled = 0;
    const colors = new Set();
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (knockout) data[i + 3] = Math.round(data[i + 3] * Math.max(0, Math.min(1, (distance(i, palette[labels[y * w + x] - 1]) - 16) / 32)));
      if (data[i + 3] > 12) {
        minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); filled++;
        if (colors.size <= 32) colors.add((data[i] >> 4) * 256 + (data[i + 1] >> 4) * 16 + (data[i + 2] >> 4));
      }
    }
    if (maxX < 0) throw Error('empty_artwork');
    if (knockout && filled / ((maxX - minX + 1) * (maxY - minY + 1)) > .94 && colors.size > 32) throw Error('background_unclear');
    const cleaned = canvas(w, h); cleaned.getContext('2d').putImageData(pixels, 0, 0);
    const cropped = canvas(maxX - minX + 1, maxY - minY + 1);
    cropped.getContext('2d').drawImage(cleaned, minX, minY, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
    return { image: cropped, backgroundRemoved: knockout, cleanup: knockout ? 'Automatically removed' : 'Transparent source' };
  }
  function fitArtwork(w, h, diameter, percent) {
    const factor = Math.min(diameter * .75 / w, diameter * .75 / h, diameter * .8 / Math.hypot(w, h)) * percent / 100;
    return { width: w * factor, height: h * factor };
  }
  function placeArtwork(w, h, diameter, percent, offsetX = 0, offsetY = 0) {
    const fitted = fitArtwork(w, h, diameter, Math.max(45, Math.min(140, percent)));
    const radius = diameter / 2 - 29, cap = Math.min(1, radius * 1.98 / Math.hypot(fitted.width, fitted.height));
    const width = fitted.width * cap, height = fitted.height * cap;
    const requestedX = (Number(offsetX) || 0) * diameter / 100, requestedY = (Number(offsetY) || 0) * diameter / 100;
    const fits = t => Math.hypot(Math.abs(requestedX * t) + width / 2, Math.abs(requestedY * t) + height / 2) <= radius;
    let low = 0, high = 1;
    if (!fits(1)) { for (let i = 0; i < 20; i++) { const mid = (low + high) / 2; if (fits(mid)) low = mid; else high = mid; } } else low = 1;
    return { width, height, x: requestedX * low, y: requestedY * low, scale: Math.max(45, Math.min(140, percent)) * cap };
  }
  function metalGradient(ctx, x, y, size, finish, color) {
    const colors = FINISHES[finish] || [color, color, color, color, color];
    const gradient = ctx.createLinearGradient(x, y, x + size * .55, y + size);
    colors.forEach((c, i) => gradient.addColorStop(i / (colors.length - 1), c));
    return gradient;
  }
  function finishLayer(image, finish, color) {
    if (finish === 'original') return image;
    const out = canvas(image.width, image.height), ctx = out.getContext('2d');
    ctx.drawImage(image.finishMask || image, 0, 0); ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = metalGradient(ctx, 0, 0, out.height, finish, color); ctx.fillRect(0, 0, out.width, out.height);
    return out;
  }
  function renderScene(target, options) {
    const ctx = target.getContext('2d'), cx = 400, cy = 298, radius = 219;
    ctx.clearRect(0, 0, 800, 640);
    if (options.previewBackground === 'custom' && options.wall) {
      const ratio = Math.max(800 / options.wall.width, 640 / options.wall.height);
      const w = options.wall.width * ratio, h = options.wall.height * ratio;
      ctx.drawImage(options.wall, (800 - w) / 2, (640 - h) / 2, w, h);
    } else if (options.previewBackground === 'black') {
      const dark = ctx.createLinearGradient(0, 0, 800, 640);
      dark.addColorStop(0, '#121318'); dark.addColorStop(1, '#050508');
      ctx.fillStyle = dark; ctx.fillRect(0, 0, 800, 640);
    } else if (options.previewBackground !== 'transparent') {
      const wall = ctx.createLinearGradient(0, 0, 800, 640);
      wall.addColorStop(0, '#e4e0dd'); wall.addColorStop(.5, '#f3f0ec'); wall.addColorStop(1, '#d7d3d0');
      ctx.fillStyle = wall; ctx.fillRect(0, 0, 800, 640);
    }
    const halo = ctx.createRadialGradient(cx, cy, radius - 5, cx, cy, radius + 73);
    halo.addColorStop(0, 'rgba(255,233,172,.95)'); halo.addColorStop(.23, 'rgba(255,237,183,.85)'); halo.addColorStop(.53, 'rgba(255,236,186,.38)'); halo.addColorStop(1, 'rgba(255,235,180,0)');
    ctx.save(); ctx.beginPath(); ctx.rect(0, 0, 800, 640);
    ctx.moveTo(cx + radius - 2, cy); ctx.arc(cx, cy, radius - 2, 0, Math.PI * 2); ctx.closePath(); ctx.clip('evenodd');
    ctx.fillStyle = halo; ctx.fillRect(0, 0, 800, 640); ctx.restore();
    if (options.artworkMode === 'full-color') {
      let source = options.artwork;
      if (!source) {
        source = canvas(420, 230); const sample = source.getContext('2d');
        sample.fillStyle = '#171719'; sample.font = '500 86px Georgia, serif'; sample.textAlign = 'center';
        const words = options.sampleWords || ['YOUR', 'LOGO'];
        sample.fillText(words[0], 210, 95); sample.fillText(words[1], 210, 199);
      }
      ctx.save(); ctx.shadowColor = 'rgba(67,47,20,.24)'; ctx.shadowBlur = 15; ctx.shadowOffsetY = 9;
      ctx.drawImage(fullColorFace(source, options.circleCrop, options.base), cx - radius, cy - radius, radius * 2, radius * 2); ctx.restore();
      return { x: cx - radius, y: cy - radius, width: radius * 2, height: radius * 2, fullColor: true };
    }
    ctx.save(); ctx.shadowColor = 'rgba(67,47,20,.24)'; ctx.shadowBlur = 15; ctx.shadowOffsetY = 9;
    ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    const surface = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
    const baseColors = { Black: ['#353437', '#121215', '#242327'], White: ['#ffffff', '#f5f3f2', '#e8e5e3'], Clear: ['rgba(255,255,255,.27)', 'rgba(241,234,219,.08)', 'rgba(255,255,255,.38)'], Frosted: ['#eeeae2', '#e3e0d9', '#f2eee6'] };
    (baseColors[options.base] || baseColors.Frosted).forEach((c, i) => surface.addColorStop(i / 2, c));
    ctx.fillStyle = surface; ctx.fill(); ctx.restore();
    ctx.strokeStyle = options.base === 'Black' ? '#e4d6ba' : 'rgba(255,255,255,.85)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, radius - 1, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = metalGradient(ctx, cx - radius, cy - radius, radius * 2, options.finish === 'original' ? 'gold' : options.finish, options.color);
    ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(cx, cy, radius - 23, 0, Math.PI * 2); ctx.stroke();
    let artwork = options.artwork;
    if (!artwork) {
      artwork = canvas(420, 230); const a = artwork.getContext('2d');
      a.fillStyle = '#111'; a.font = '500 86px Georgia, serif'; a.textAlign = 'center';
      const words = options.sampleWords || ['YOUR', 'LOGO'];
      a.fillText(words[0], 210, 95); a.fillText(words[1], 210, 199);
    }
    const fitted = placeArtwork(artwork.width, artwork.height, radius * 2, options.scale || 100, (options.offsetX || 0) / 438 * 100, (options.offsetY || 0) / 438 * 100);
    const x = cx - fitted.width / 2 + fitted.x, y = cy - fitted.height / 2 + fitted.y;
    const layer = finishLayer(artwork, options.finish, options.color);
    ctx.save(); ctx.shadowColor = options.base === 'Black' ? '#000' : 'rgba(73,47,15,.48)'; ctx.shadowBlur = 3; ctx.shadowOffsetX = 2; ctx.shadowOffsetY = 4;
    ctx.drawImage(layer, x, y, fitted.width, fitted.height); ctx.restore();
    for (const sx of [cx - radius + 11, cx + radius - 11]) {
      ctx.save(); ctx.shadowColor = 'rgba(0,0,0,.25)'; ctx.shadowBlur = 4; ctx.shadowOffsetY = 2;
      ctx.fillStyle = metalGradient(ctx, sx - 8, cy - 8, 16, 'gold', options.color);
      ctx.beginPath(); ctx.arc(sx, cy, 8, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      ctx.strokeStyle = 'rgba(99,67,25,.55)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(sx - 3, cy - 2); ctx.lineTo(sx + 3, cy + 2); ctx.stroke();
    }
    const bounds = { x, y, width: fitted.width, height: fitted.height, handleX: x + fitted.width + 12, handleY: y + fitted.height + 12 };
    if (options.editable) {
      ctx.save(); ctx.setLineDash([7, 5]); ctx.strokeStyle = 'rgba(111, 57, 152, .8)'; ctx.lineWidth = 2;
      ctx.strokeRect(x - 9, y - 9, fitted.width + 18, fitted.height + 18);
      ctx.setLineDash([]); ctx.fillStyle = '#ffffff'; ctx.strokeStyle = '#6f3998'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(bounds.handleX, bounds.handleY, 17, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(bounds.handleX - 7, bounds.handleY + 5);
      ctx.lineTo(bounds.handleX + 5, bounds.handleY - 7); ctx.stroke(); ctx.restore();
    }
    return bounds;
  }
  function decodeFile(file) {
    return new Promise((resolve, reject) => {
      const image = new Image(), url = URL.createObjectURL(file);
      const finish = () => { clearTimeout(timer); image.onload = null; image.onerror = null; URL.revokeObjectURL(url); };
      const timer = setTimeout(() => { finish(); reject(Error('file_decode')); }, 20000);
      image.onload = () => { try { const source = sourceCanvas(image); finish(); resolve(source); } catch (error) { finish(); reject(error); } };
      image.onerror = () => { finish(); reject(Error('file_decode')); };
      image.src = url;
    });
  }
  // A rejected/cancelled replacement never commits over the accepted source.
  class UploadState {
    constructor() { this.file = null; this.source = null; this.artwork = null; this.cleanup = ''; this.crop = null; this.version = 0; this.pending = false; }
    async select(file, prepare) {
      if (!file) return false;
      const version = ++this.version; this.pending = true;
      try {
        validateFile(file);
        const result = await prepare(file);
        if (version !== this.version) return false;
        this.file = file; this.source = result.source; this.artwork = result.artwork; this.cleanup = result.cleanup; this.crop = null; return true;
      } finally { if (version === this.version) this.pending = false; }
    }
    clear() { this.version++; this.file = null; this.source = null; this.artwork = null; this.cleanup = ''; this.crop = null; this.pending = false; }
  }
  const digest = async value => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', value)), b => b.toString(16).padStart(2, '0')).join('');
  function attachmentURL(value) {
    if (typeof value !== 'string') return false;
    try {
      const url = new URL(value, 'https://miamineons.com');
      return url.protocol === 'https:' && !url.username && !url.password && !url.port && (
        url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/') ||
        ['miamineons.com', 'www.miamineons.com', 'te0j25-2a.myshopify.com'].includes(url.hostname) && url.pathname.startsWith('/uploads/'));
    } catch { return false; }
  }
  async function orderBundle(options) {
    validateFile(options.file); if (!options.file) throw Error('need_artwork');
    const artworkMode = options.artworkMode || 'decorative', fullColor = artworkMode === 'full-color';
    if (!['decorative', 'full-color'].includes(artworkMode)) throw Error('cart_error');
    const crop = fullColor && options.circleCrop ? circleCrop(options.circleCrop) : null;
    if (fullColor && (!options.face || options.face.type !== 'image/png' || !options.face.size ||
        !options.sourceDimensions || ![options.sourceDimensions.width, options.sourceDimensions.height].every(n => Number.isInteger(n) && n > 0 && n <= 16000))) throw Error('cart_error');
    const diameter = Number.parseFloat(options.variant.diameter);
    if (![12, 18, 24, 30, 36, 40].includes(diameter) || !['Frosted', 'Clear', 'White', 'Black'].includes(options.variant.base)) throw Error('cart_error');
    const id = options.id || crypto.randomUUID();
    const originalHash = await digest(await options.file.arrayBuffer());
    const previewHash = await digest(await options.preview.arrayBuffer());
    const wallHash = options.wallFile ? await digest(await options.wallFile.arrayBuffer()) : '';
    const faceHash = fullColor ? await digest(await options.face.arrayBuffer()) : '';
    const state = { designMode: 'logo', productStyle: 'acrylic-backlit', artworkMode, circleCrop: crop, logo: { name: options.file.name },
      width: diameter, height: diameter, base: options.variant.base, finish: options.finish, color: options.color,
      scale: options.scale, offsetX: options.offsetX, offsetY: options.offsetY, cleanup: options.cleanup,
      ...(fullColor ? { sourceDimensions: { ...options.sourceDimensions }, cropCoordinates: 'Normalized original image; zoom 1 fits its diagonal inside 98% of the sign diameter' } : {}),
      previewBackground: options.background, wallPhoto: options.wallFile?.name || '', wallPhotoPurpose: 'Preview only; never printed or used as production artwork' };
    const source = { schema: 'miami-neons-customizer-design', version: 1, design_id: id, units: 'in',
      width: diameter, height: diameter, state, approval: 'pending', production: 'Acrylic backlit sign. Final proof and fabrication files require review before manufacturing.',
      preview_sha256: previewHash, original_sha256: originalHash, reference_background_sha256: wallHash,
      ...(faceHash ? { flat_face_sha256: faceHash, flat_face_purpose: 'Flat circular face preview without wall or lighting; reproduce from the original artwork and saved crop for fabrication' } : {}) };
    const sourceFile = new File([JSON.stringify(source)], 'miami-neons-backlit-' + id + '.json', { type: 'application/json' });
    const props = { 'Sign style': fullColor ? 'Full-color round acrylic backlit' : 'Round acrylic backlit', 'Dimensions': diameter + ' × ' + diameter + ' in',
      'Backing': 'Round acrylic · ' + options.variant.base, 'Lighting': 'Warm-white rear LED halo', 'Location': 'Indoor',
      'Mounting': 'Wall spacers', 'Logo': options.file.name, 'Logo finish': options.finish, 'Logo color': options.color,
      'Logo preview size': fullColor ? (crop ? Math.round(crop.zoom * 100) + '% circle crop' : 'Whole logo') : options.scale + '%',
      'Logo preview position': fullColor ? (crop ? 'Custom circular crop' : 'Centered') : 'X ' + options.offsetX + ', Y ' + options.offsetY + ' canvas px',
      'Artwork background cleanup': options.cleanup, 'Preview background': options.background === 'custom' ? 'Customer wall photo — preview only' : options.background,
      'Artwork approval': 'Final proof required before fabrication', 'Design reference': id, '_mn_design_id': id,
      '_mn_design_json': JSON.stringify(state), '_mn_artwork_mode': artworkMode, '_mn_preview_sha256': previewHash, '_mn_original_sha256': originalHash,
      '_mn_design_source_sha256': await digest(await sourceFile.arrayBuffer()), '_mn_production_status': source.production,
      ...(fullColor ? { 'Logo style': 'Full-color logo', 'Circle crop': crop ? 'Custom circle crop' : 'Whole logo', '_mn_circle_crop': JSON.stringify(crop), '_mn_flat_face_sha256': faceHash } : {}),
      ...(wallHash ? { '_mn_reference_background_sha256': wallHash } : {}) };
    return { id, props, file: options.file, preview: options.preview, sourceFile, wallFile: options.wallFile, face: fullColor ? options.face : null,
      variantId: String(options.variant.id), price: options.variant.price };
  }
  function orderForm(form, bundle) {
    form.set('id', bundle.variantId);
    for (const [key, value] of Object.entries(bundle.props)) form.set('properties[' + key + ']', value);
    form.delete('properties[Logo artwork]');
    for (const [key, file] of [['Design preview', bundle.preview], ['_mn_original_artwork', bundle.file], ['_mn_design_source', bundle.sourceFile], ['_mn_reference_background', bundle.wallFile], ['_mn_flat_face', bundle.face]]) {
      if (file) form.set('properties[' + key + ']', file);
    }
    return form;
  }
  // The cart owns pricing and payment. Verify the exact design attachments before
  // moving to it; retries reuse this design ID and never merge two custom signs.
  async function addDesignToCart(bundle, form, transport = fetch, root = '/') {
    const quantity = Number(form.get('quantity') || 1);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10000) throw Error('cart_error');
    const read = async () => { const r = await transport(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' }); if (!r.ok) throw Error('cart_error'); return r.json(); };
    let current = await read();
    if (!current.items.some(item => item.properties?._mn_design_id === bundle.id)) {
      const r = await transport(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', body: orderForm(form, bundle) });
      const result = await r.json(); if (!r.ok || result.status) throw Error('cart_error');
      current = await read();
    }
    const lines = current.items.filter(item => item.properties?._mn_design_id === bundle.id), line = lines[0];
    const required = ['Design preview', '_mn_original_artwork', '_mn_design_source', ...(bundle.wallFile ? ['_mn_reference_background'] : []), ...(bundle.face ? ['_mn_flat_face'] : [])];
    if (lines.length !== 1 || String(line.variant_id) !== bundle.variantId || line.quantity !== quantity ||
        current.currency !== 'USD' || line.original_line_price !== bundle.price * quantity ||
        required.some(key => !attachmentURL(line.properties[key])) ||
        Object.entries(bundle.props).some(([key, value]) => line.properties[key] !== value)) {
      const updates = Object.fromEntries(lines.map(item => [item.key, 0]));
      if (lines.length) { const r = await transport(root + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) }); if (!r.ok) throw Error('cart_review'); }
      throw Error('cart_error');
    }
    return current;
  }
  const api = { validateFile, sourceCanvas, prepareArtwork, fitArtwork, placeArtwork, finishLayer, renderScene, UploadState, orderBundle, orderForm, addDesignToCart,
    circleCrop, circlePlacement, fullColorFace, artworkForMode, CircleCropEditor };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof customElements === 'undefined' || customElements.get('mn-backlit-customizer')) return;
  customElements.define('mn-backlit-customizer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const target = this.querySelector('canvas');
      if (!target || !target.getContext('2d')) return;
      this.initialized = true; this.target = target; this.upload = new UploadState(); this.wall = new UploadState(); this.offsetX = 0; this.offsetY = 0; this.pointer = null; this.revision = 0; this.mode = 'decorative';
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = document.getElementById(this.dataset.formId);
      this.cropEditor = new CircleCropEditor(this.querySelector('[data-circle-dialog]'), this.strings);
      this.querySelectorAll('[data-artwork-mode]').forEach(input => input.addEventListener('change', () => this.changeMode(input.value)));
      this.querySelector('[data-circle-open]').addEventListener('click', () => this.editCircle());
      this.querySelector('[data-circle-remove]').addEventListener('click', () => { this.upload.crop = null; this.update(); });
      this.bindPicker(this.querySelector('[data-picker]'));
      this.bindWallPicker(this.querySelector('[data-wall-picker]'));
      this.querySelector('[data-wall-remove]').addEventListener('click', () => {
        this.wall.clear(); this.querySelector('[data-view-modes] input[value="light"]').checked = true;
        this.wallStatus(''); this.update();
      });
      this.querySelector('[data-remove]').addEventListener('click', () => {
        this.cropEditor.cancel();
        this.upload.clear(); this.offsetX = 0; this.offsetY = 0; this.querySelector('[data-scale]').value = 100; if (this.acceptedInput) this.acceptedInput.remove(); this.acceptedInput = null;
        this.setStatus(''); this.update();
      });
      this.querySelectorAll('.mnbl__finishes input').forEach(el => el.addEventListener('change', () => this.update()));
      this.querySelector('[data-custom-color]').addEventListener('input', () => this.update());
      this.querySelector('[data-scale]').addEventListener('input', () => this.update());
      this.querySelectorAll('[data-view-modes] input').forEach(el => el.addEventListener('change', () => this.update()));
      this.bindCanvas();
      this.querySelector('[data-base]').addEventListener('change', event => this.selectBase(event.target.value));
      this.querySelectorAll('[data-move]').forEach(button => button.addEventListener('click', () => {
        const direction = button.dataset.move;
        if (direction === 'center') { this.offsetX = 0; this.offsetY = 0; }
        else { this.offsetX += direction === 'left' ? -8 : direction === 'right' ? 8 : 0; this.offsetY += direction === 'up' ? -8 : direction === 'down' ? 8 : 0; }
        this.update();
      }));
      target.addEventListener('keydown', event => {
        if (this.busy || this.mode === 'full-color' || !this.upload.file || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home'].includes(event.key)) return;
        event.preventDefault(); const step = event.shiftKey ? 20 : 4;
        if (event.key === 'Home') { this.offsetX = 0; this.offsetY = 0; }
        else { this.offsetX += event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0; this.offsetY += event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0; }
        this.update();
      });
      this.form.addEventListener('input', event => { if (event.target.name === 'id') this.update(); });
      this.form.addEventListener('submit', event => { event.preventDefault(); event.stopImmediatePropagation(); this.checkout(); }, true);
      this.update();
    }
    selectBase(value) {
      const picker = this.form.closest('.m-main-product--info')?.querySelector('variant-picker');
      if (!picker) return;
      const input = [...picker.querySelectorAll('[data-option-position="2"] input[type="radio"]')].find(el => el.value === value);
      if (input) input.click();
      else {
        const select = picker.querySelector('select[name="options[Acrylic Base]"]');
        if (select) { select.value = value; select.dispatchEvent(new Event('change', { bubbles: true })); }
      }
    }
    changeMode(mode) {
      if (this.busy || !['decorative', 'full-color'].includes(mode) || this.cropEditor.active) return;
      try {
        if (this.upload.source) {
          const prepared = artworkForMode(this.upload.source, mode);
          this.upload.artwork = prepared.image; this.upload.cleanup = prepared.cleanup;
        }
        this.mode = mode; this.pointer = null;
        if (mode === 'full-color') this.selectBase('White');
        this.setStatus(this.upload.file?.name || '');
      } catch (error) { this.setStatus(this.strings[error.message] || this.strings.file_decode, true); }
      this.querySelectorAll('[data-artwork-mode]').forEach(input => { input.checked = input.value === this.mode; });
      this.update();
    }
    async editCircle() {
      if (this.mode !== 'full-color' || !this.upload.source || this.upload.pending || this.busy) return;
      const version = this.upload.version, crop = await this.cropEditor.edit(this.upload.source, this.upload.crop, this.variant.base);
      if (crop && version === this.upload.version && this.upload.file) { this.upload.crop = Object.freeze(circleCrop(crop)); this.update(); }
    }
    bindCanvas() {
      const position = event => {
        const rect = this.target.getBoundingClientRect();
        return { x: (event.clientX - rect.left) * this.target.width / rect.width,
          y: (event.clientY - rect.top) * this.target.height / rect.height };
      };
      this.target.addEventListener('pointerdown', event => {
        if (this.busy || this.mode === 'full-color' || !this.upload.artwork || !this.bounds || this.pointer) return;
        const at = position(event), b = this.bounds;
        const resize = Math.hypot(at.x - b.handleX, at.y - b.handleY) < 52;
        const move = at.x >= b.x - 20 && at.x <= b.x + b.width + 20 && at.y >= b.y - 20 && at.y <= b.y + b.height + 20;
        if (!resize && !move) return;
        this.target.focus({ preventScroll: true });
        this.pointer = { id: event.pointerId, mode: resize ? 'resize' : 'move', at,
          x: this.offsetX, y: this.offsetY, scale: Number(this.querySelector('[data-scale]').value),
          radius: Math.max(20, Math.hypot(at.x - 400 - this.offsetX, at.y - 298 - this.offsetY)) };
        this.target.setPointerCapture(event.pointerId); event.preventDefault();
      });
      this.target.addEventListener('pointermove', event => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        const at = position(event), pointer = this.pointer;
        if (pointer.mode === 'move') {
          this.offsetX = pointer.x + at.x - pointer.at.x;
          this.offsetY = pointer.y + at.y - pointer.at.y;
        } else {
          const distance = Math.hypot(at.x - 400 - pointer.x, at.y - 298 - pointer.y);
          this.querySelector('[data-scale]').value = Math.max(45, Math.min(140, Math.round(pointer.scale * distance / pointer.radius)));
        }
        this.update(); event.preventDefault();
      });
      const release = event => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        this.pointer = null;
        if (this.target.hasPointerCapture(event.pointerId)) this.target.releasePointerCapture(event.pointerId);
      };
      this.target.addEventListener('pointerup', release);
      this.target.addEventListener('pointercancel', release);
      this.target.addEventListener('lostpointercapture', () => { this.pointer = null; });
    }
    bindPicker(input) {
      input.removeAttribute('name');
      input.addEventListener('change', async () => {
        const file = input.files && input.files[0];
        if (!file) return;
        input.disabled = true; this.setStatus(this.strings.loading);
        try {
          const accepted = await this.upload.select(file, async selected => {
            const source = await decodeFile(selected);
            const prepared = artworkForMode(source, this.mode);
            return { source, artwork: prepared.image, cleanup: prepared.cleanup };
          });
          if (accepted) {
            this.cropEditor.cancel();
            this.offsetX = 0; this.offsetY = 0; this.querySelector('[data-scale]').value = 100;
            // Retain the untouched native File input for multipart cart attachment.
            // The replacement picker is a fresh input; opening/cancelling it cannot clear the accepted file.
            if (this.acceptedInput) this.acceptedInput.remove();
            const next = input.cloneNode(false); next.disabled = false; next.value = ''; next.removeAttribute('name');
            input.parentNode.appendChild(next); input.removeAttribute('data-picker'); input.hidden = true;
            input.name = 'properties[Logo artwork]'; input.disabled = false;
            this.appendChild(input); this.acceptedInput = input; this.bindPicker(next);
            this.setStatus(file.name);
          }
        } catch (error) { this.setStatus(this.strings[error.message] || this.strings.file_decode, true); }
        finally { input.disabled = false; this.update(); }
      });
    }
    bindWallPicker(input) {
      input.addEventListener('change', async () => {
        const file = input.files && input.files[0]; if (!file) return;
        input.disabled = true; this.wallStatus(this.strings.loading);
        try {
          const accepted = await this.wall.select(file, async selected => { const source = await decodeFile(selected); return { source, artwork: source, cleanup: 'Preview only' }; });
          if (accepted) {
            this.querySelector('[data-view-modes] input[value="custom"]').checked = true;
            this.wallStatus(file.name);
          }
        } catch (error) { this.wallStatus(this.strings[error.message] || this.strings.file_decode, true); }
        finally { input.disabled = false; input.value = ''; this.update(); }
      });
    }
    wallStatus(text, error) {
      const el = this.querySelector('[data-wall-status]'); el.textContent = text; el.dataset.error = error ? 'true' : 'false';
    }
    lockDesignControls() {
      this.lockedControls = new Map();
      const area = this.form.closest('.m-main-product--info') || this;
      area.querySelectorAll('input, select, button, textarea').forEach(input => { this.lockedControls.set(input, input.disabled); input.disabled = true; });
      this.pointer = null; this.target.setAttribute('aria-disabled', 'true');
    }
    unlockDesignControls() {
      if (this.lockedControls) { this.lockedControls.forEach((disabled, input) => { input.disabled = disabled; }); this.lockedControls.clear(); }
      this.target.removeAttribute('aria-disabled');
    }
    async checkout() {
      if (this.busy) return;
      if (this.cropEditor.active) { this.cropEditor.target.focus(); return this.setStatus(this.strings.crop_finish, true); }
      if (this.upload.pending || this.wall.pending) return this.setStatus(this.strings.wait_upload, true);
      if (!this.upload.file) { this.setStatus(this.strings.need_artwork, true); this.querySelector('[data-picker]').focus(); return; }
      if (this.querySelector('[data-view-modes] input:checked').value === 'custom' && !this.wall.file) {
        this.wallStatus(this.strings.wall_upload, true); this.querySelector('[data-wall-picker]').focus(); return;
      }
      this.busy = true;
      const button = this.form.querySelector('[type="submit"][name="add"]');
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      this.setStatus(this.strings.preparing_cart);
      try {
        const revision = this.revision, options = { ...this.sceneOptions, circleCrop: this.sceneOptions.circleCrop ? circleCrop(this.sceneOptions.circleCrop) : null, editable: false }, form = new FormData(this.form);
        const originalFile = this.upload.file, wallFile = options.previewBackground === 'custom' ? this.wall.file : null;
        const sourceDimensions = { width: this.upload.source.originalWidth || this.upload.source.width, height: this.upload.source.originalHeight || this.upload.source.height };
        const variant = { ...this.variant }, cleanup = this.upload.cleanup;
        this.lockDesignControls();
        // Capture the preview before any asynchronous work, without editing handles.
        if (!this.bundle || this.bundleRevision !== revision) {
          const scene = canvas(800, 640); renderScene(scene, options);
          const flat = options.artworkMode === 'full-color' ? fullColorFace(options.artwork, options.circleCrop, options.base) : null;
          const [blob, faceBlob] = await Promise.all([new Promise(resolve => scene.toBlob(resolve, 'image/png')),
            flat ? new Promise(resolve => flat.toBlob(resolve, 'image/png')) : Promise.resolve(null)]);
          if (!blob) throw Error('cart_error');
          if (flat && !faceBlob) throw Error('cart_error');
          const bundle = await orderBundle({ file: originalFile, preview: new File([blob], 'miami-neons-backlit-preview.png', { type: 'image/png' }),
            face: faceBlob ? new File([faceBlob], 'miami-neons-flat-circle-face.png', { type: 'image/png' }) : null,
            artworkMode: options.artworkMode, circleCrop: options.circleCrop, sourceDimensions, wallFile, variant,
            finish: options.artworkMode === 'full-color' ? 'Original full color' : options.finish === 'custom' ? 'Custom color' : options.finish.charAt(0).toUpperCase() + options.finish.slice(1),
            color: options.color, scale: options.scale,
            offsetX: options.offsetX, offsetY: options.offsetY, cleanup, background: options.previewBackground });
          if (revision !== this.revision) throw Error('design_changed');
          this.bundle = bundle; this.bundleRevision = revision;
        }
        const root = window.Shopify?.routes?.root || '/';
        await addDesignToCart(this.bundle, form, fetch, root);
        window.location.assign(root + 'cart');
      } catch (error) { this.setStatus(this.strings[error.message] || this.strings.cart_error, true); }
      finally { this.unlockDesignControls(); this.busy = false; if (button) { button.disabled = false; button.removeAttribute('aria-busy'); } }
    }
    setStatus(text, error) {
      const status = this.querySelector('[data-status]'); status.textContent = text; status.dataset.error = error ? 'true' : 'false';
    }
    update() {
      this.revision++;
      const fullColor = this.mode === 'full-color';
      const finish = fullColor ? 'original' : this.querySelector('.mnbl__finishes input:checked').value;
      const color = finish === 'custom' ? this.querySelector('[data-custom-color]').value : ({ gold: '#cba34f', silver: '#b8bec9', black: '#202023', white: '#ffffff', original: 'original' })[finish];
      const variantId = this.form.querySelector('[name="id"]').value;
      const variant = this.variants.find(v => String(v.id) === variantId) || this.variants[0];
      this.variant = variant;
      const hasUpload = !!this.upload.file;
      let scale = Number(this.querySelector('[data-scale]').value);
      if (hasUpload && !fullColor) {
        const placement = placeArtwork(this.upload.artwork.width, this.upload.artwork.height, 438, scale, this.offsetX / 438 * 100, this.offsetY / 438 * 100);
        this.offsetX = placement.x; this.offsetY = placement.y;
        scale = Math.floor(placement.scale); this.querySelector('[data-scale]').value = String(scale);
      }
      this.target.tabIndex = hasUpload && !fullColor ? 0 : -1;
      this.dataset.artworkMode = this.mode;
      this.querySelector('[data-base]').value = variant.base;
      this.querySelector('[data-configuration]').textContent = variant.diameter + ' · ' + variant.base;
      this.querySelector('[data-finish-label]').textContent = this.strings[finish];
      this.querySelector('.mnbl__finishes').hidden = fullColor;
      this.querySelector('[data-full-color-note]').hidden = !fullColor;
      this.querySelector('[data-circle-actions]').hidden = !fullColor || !hasUpload;
      this.querySelector('[data-circle-open]').textContent = this.strings[this.upload.crop ? 'crop_edit' : 'crop_open'];
      this.querySelector('[data-circle-remove]').hidden = !this.upload.crop;
      this.querySelector('[data-custom-color-row]').hidden = fullColor || finish !== 'custom';
      this.querySelector('[data-color-value]').textContent = color.toUpperCase();
      this.querySelector('[data-adjustments]').hidden = !hasUpload || fullColor;
      this.querySelector('[data-move-tip]').hidden = !hasUpload || fullColor;
      this.querySelector('[data-cleanup-note]').hidden = !hasUpload || this.upload.cleanup !== 'Needs review';
      this.querySelector('[data-stage]').dataset.view = this.querySelector('[data-view-modes] input:checked').value;
      const customWall = this.querySelector('[data-stage]').dataset.view === 'custom';
      this.querySelector('[data-wall-upload]').hidden = !customWall;
      this.querySelector('[data-wall-remove]').hidden = !this.wall.file;
      this.querySelector('[data-wall-label]').textContent = this.strings[this.wall.file ? 'wall_replace' : 'wall_upload'];
      this.querySelector('[data-wall-picker]').setAttribute('aria-label', this.strings[this.wall.file ? 'wall_replace' : 'wall_upload']);
      this.dataset.hasArtwork = hasUpload ? 'true' : 'false';
      this.querySelector('[data-scale-label]').textContent = scale + '%';
      this.querySelector('[data-remove]').hidden = !hasUpload;
      this.querySelector('[data-upload-label]').textContent = this.strings[hasUpload ? 'replace' : 'upload'];
      this.querySelector('[data-picker]').setAttribute('aria-label', this.strings[hasUpload ? 'replace' : 'upload']);
      for (const [selector, value] of [['[data-finish-property]', fullColor ? 'Original full color' : finish === 'custom' ? 'Custom color' : finish.charAt(0).toUpperCase() + finish.slice(1)], ['[data-color-property]', color], ['[data-scale-property]', fullColor ? 'Whole logo or selected circle crop' : scale + '%'], ['[data-background-property]', hasUpload ? this.upload.cleanup : 'Automatic']]) {
        const input = this.querySelector(selector); input.value = value; input.disabled = !hasUpload;
      }
      this.querySelector('[data-position-property]').value = hasUpload ? 'X ' + Math.round(this.offsetX / 219 * 100) + '%, Y ' + Math.round(this.offsetY / 219 * 100) + '%' : 'Centered';
      this.target.setAttribute('aria-label', hasUpload ? this.strings.uploaded_alt + ' ' + this.upload.file.name + ', ' + this.strings[finish] + ', ' + variant.base : this.strings.sample_alt);
      this.sceneOptions = { base: variant.base, finish, color, scale: fullColor ? 100 : scale, artwork: fullColor ? this.upload.source : this.upload.artwork,
        artworkMode: this.mode, circleCrop: fullColor && this.upload.crop ? circleCrop(this.upload.crop) : null,
        offsetX: fullColor ? 0 : this.offsetX, offsetY: fullColor ? 0 : this.offsetY, editable: hasUpload && !fullColor,
        previewBackground: this.querySelector('[data-view-modes] input:checked').value,
        wall: this.wall.source, sampleWords: [this.strings.your, this.strings.logo] };
      this.bounds = renderScene(this.target, this.sceneOptions);
    }
  });
})();
